import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { io, Socket } from "socket.io-client";
import { APP_BASE_URL } from "@utils/request";
import { Helper } from "@utils/index";
import { toast } from "react-toastify";

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  unreadChatCount: number;
  resetUnreadChatCount: () => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  unreadChatCount: 0,
  resetUnreadChatCount: () => {},
});

export const useSocket = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  const resetUnreadChatCount = useCallback(() => {
    setUnreadChatCount(0);
  }, []);

  useEffect(() => {
    const user = Helper.getUser();
    if (!user?.token) return;

    const socketUrl = (APP_BASE_URL || "").includes("localhost")
      ? "http://localhost:9001"
      : (APP_BASE_URL || "").replace("/api", "").replace(":8000", ":9001");
    // const socketUrl = "wss://app.zedfo.com";

    console.log("🔌 Connecting to Socket.IO server at:", socketUrl);

    const newSocket = io(socketUrl, {
      path: "/socket",
      auth: {
        token: user.token,
      },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
    });

    newSocket.on("connect", () => {
      console.log("✅ Chat socket connected");
      setIsConnected(true);
    });

    newSocket.on("disconnect", () => {
      console.log("❌ Chat socket disconnected");
      setIsConnected(false);
    });

    // Global Chat Notifications
    newSocket.on("chat:message_notification", (message: any) => {
      // Don't show toast if we are on the chat page for this conversation
      const isChatPage = window.location.pathname.includes("/chat");
      if (isChatPage) return;

      toast.info(
        <div
          style={{ cursor: "pointer" }}
          onClick={() =>
            (window.location.href = `/admin/support?id=${message.conversationId}`)
          }
        >
          <div style={{ fontWeight: "bold", fontSize: "13px" }}>
            New Support Message
          </div>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>
            {message.content?.substring(0, 50)}...
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    });

    newSocket.on("chat:new_message", (message: any) => {
      const user = Helper.getUser();
      if (message.senderId === user?.id) return;

      // Increment unread count if not on chat page
      const isChatPage = window.location.pathname.includes("/chat");
      if (!isChatPage) {
        setUnreadChatCount((prev) => prev + 1);
      }

      // 👇 If tab is NOT visible → update title
      if (document.visibilityState === "hidden") {
        document.title = `🔴 New Message - ${message.content?.substring(0, 20)}...`;
        return;
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{ socket, isConnected, unreadChatCount, resetUnreadChatCount }}
    >
      {children}
    </SocketContext.Provider>
  );
};
